import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  Stack,
} from "@mui/material";
import {
  Person,
  PersonOff,
  Settings,
  ContentCopy,
  ContentPaste,
  Save,
  Undo,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Schedule,
  Close,
  LightMode,
  WbTwilight,
  Nightlight,
  DarkMode,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import {
  Employee,
  DateAvailability,
  TimeRestrictions,
} from "../../types/employee.types";
import { DayOfWeek } from "../../types/common.types";
import { useShops } from "../../hooks/useShops";
import { useEmployees } from "../../hooks/useEmployees";
import { employeesService } from "../../services/firebase/employees.service";
import DateNavigator from "../common/DateNavigator";

interface EmployeeAvailabilityCalendarProps {
  employees: Employee[];
  isLoading: boolean;
  canWrite: boolean;
  allowEditHistory?: boolean;
}

const dayOfWeekOrder: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const dayLabels: Record<DayOfWeek, string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

export default function EmployeeAvailabilityCalendar({
  employees,
  isLoading,
  canWrite,
  allowEditHistory = false,
}: EmployeeAvailabilityCalendarProps) {
  const { shops } = useShops();
  const { updateMultipleDateAvailability } = useEmployees();
  const { enqueueSnackbar } = useSnackbar();

  // Week navigation state (Monday-based)
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : -(day - 1);
    const start = new Date(today);
    start.setDate(today.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    return start;
  });

  // Local state for hours (to avoid constant API calls)
  const [hoursState, setHoursState] = useState<Record<string, number | string>>(
    {}
  );

  // Local state for preferences (time restrictions and notes)
  const [preferencesState, setPreferencesState] = useState<
    Record<string, { timeRestrictions?: TimeRestrictions; notes?: string }>
  >({});

  // Preferences dialog state
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [preferencesForm, setPreferencesForm] = useState({
    timeRestrictions: {
      morning: false,
      afternoon: false,
      evening: false,
      night: false,
    },
    notes: "",
  });

  // Copy/Paste state
  const [copiedEmployeeData, setCopiedEmployeeData] = useState<{
    employeeId: string;
    employeeName: string;
    weekStart: Date;
    weekStartDate: string;
    weekEndDate: string;
    weekNumber: number;
    availability: Record<string, DateAvailability>;
  } | null>(null);

  const [copiedWeekData, setCopiedWeekData] = useState<{
    weekStart: Date;
    weekStartDate: string;
    weekEndDate: string;
    weekNumber: number;
    employeesAvailability: Record<string, Record<string, DateAvailability>>;
  } | null>(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Original state for revert functionality
  const [originalHoursState, setOriginalHoursState] = useState<
    Record<string, number | string>
  >({});

  // Original preferences state for revert functionality
  const [originalPreferencesState, setOriginalPreferencesState] = useState<
    Record<string, { timeRestrictions?: TimeRestrictions; notes?: string }>
  >({});

  // Track if there are unsaved changes
  const [isSaving, setIsSaving] = useState(false);

  // Track if we've initialized state for the current week (prevents re-initialization on data refetch)
  const initializedWeekRef = useRef<string | null>(null);

  // Check if there are unsaved changes (memoized for navigation blocking)
  const hasUnsavedChanges = useCallback((): boolean => {
    // Check hours changes
    const currentKeys = Object.keys(hoursState);
    const originalKeys = Object.keys(originalHoursState);

    if (currentKeys.length !== originalKeys.length) return true;

    for (const key of currentKeys) {
      const currentVal = hoursState[key]?.toString() || "";
      const originalVal = originalHoursState[key]?.toString() || "";
      if (currentVal !== originalVal) return true;
    }

    for (const key of originalKeys) {
      if (!(key in hoursState)) return true;
    }

    // Check preferences changes
    const currentPrefKeys = Object.keys(preferencesState);
    const originalPrefKeys = Object.keys(originalPreferencesState);

    if (currentPrefKeys.length !== originalPrefKeys.length) return true;

    for (const key of currentPrefKeys) {
      const current = preferencesState[key];
      const original = originalPreferencesState[key];
      if (!original) return true;
      if (JSON.stringify(current) !== JSON.stringify(original)) return true;
    }

    for (const key of originalPrefKeys) {
      if (!(key in preferencesState)) return true;
    }

    return false;
  }, [
    hoursState,
    originalHoursState,
    preferencesState,
    originalPreferencesState,
  ]);

  // Sync unsaved changes state with context for navigation blocking
  const { setHasUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges());
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Warn user when trying to leave with unsaved changes (browser refresh/close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Helper to format date as YYYY-MM-DD (using local timezone)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper to get ISO week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  // Helper to format date range
  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleDateString("en-US", { month: "short" });
    const endMonth = end.toLocaleDateString("en-US", { month: "short" });
    const year = end.getFullYear();
    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} ${year}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  };

  // Clear all clipboard data
  const clearAllClipboard = () => {
    setCopiedEmployeeData(null);
    setCopiedWeekData(null);
  };

  // Helper to create state key
  const getStateKey = (employeeId: string, date: Date): string => {
    return `${employeeId}_${formatDate(date)}`;
  };

  // Get dates for the current week (memoized to prevent recreation on every render)
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekStart]);

  // Initialize hours and preferences state from employee data
  // Only reinitialize when weekStart changes, not when employees data is refetched
  const weekKey = formatDate(weekStart);
  useEffect(() => {
    // Skip if we've already initialized state for this week
    // This prevents UI flicker when data is refetched after save
    if (initializedWeekRef.current === weekKey) {
      return;
    }

    const newHoursState: Record<string, number | string> = {};
    const newPrefsState: Record<
      string,
      { timeRestrictions?: TimeRestrictions; notes?: string }
    > = {};
    employees.forEach((employee) => {
      weekDates.forEach((date) => {
        const key = getStateKey(employee.id, date);
        const dateKey = formatDate(date);
        const availability = employeesService.getDateAvailability(
          employee,
          dateKey
        );
        if (availability) {
          newHoursState[key] = availability.hoursAvailable;
          if (availability.timeRestrictions || availability.notes) {
            newPrefsState[key] = {
              timeRestrictions: availability.timeRestrictions,
              notes: availability.notes,
            };
          }
        }
      });
    });
    setHoursState(newHoursState);
    setOriginalHoursState(newHoursState);
    setPreferencesState(newPrefsState);
    setOriginalPreferencesState(newPrefsState);

    // Mark this week as initialized
    initializedWeekRef.current = weekKey;
  }, [employees, weekKey, weekDates]);

  // Check if there are unsaved changes
  const hasChanges = (): boolean => {
    // Check hours changes
    const currentKeys = Object.keys(hoursState);
    const originalKeys = Object.keys(originalHoursState);

    if (currentKeys.length !== originalKeys.length) return true;

    for (const key of currentKeys) {
      const currentVal = hoursState[key]?.toString() || "";
      const originalVal = originalHoursState[key]?.toString() || "";
      if (currentVal !== originalVal) return true;
    }

    for (const key of originalKeys) {
      if (!(key in hoursState)) return true;
    }

    // Check preferences changes
    const currentPrefKeys = Object.keys(preferencesState);
    const originalPrefKeys = Object.keys(originalPreferencesState);

    if (currentPrefKeys.length !== originalPrefKeys.length) return true;

    for (const key of currentPrefKeys) {
      const current = preferencesState[key];
      const original = originalPreferencesState[key];
      if (!original) return true;
      if (JSON.stringify(current) !== JSON.stringify(original)) return true;
    }

    for (const key of originalPrefKeys) {
      if (!(key in preferencesState)) return true;
    }

    return false;
  };

  // Get count of changes
  const getChangesCount = (): number => {
    let count = 0;
    const allKeys = new Set([
      ...Object.keys(hoursState),
      ...Object.keys(originalHoursState),
    ]);

    for (const key of allKeys) {
      const currentVal = hoursState[key]?.toString() || "";
      const originalVal = originalHoursState[key]?.toString() || "";
      if (currentVal !== originalVal) count++;
    }

    // Count preferences changes
    const allPrefKeys = new Set([
      ...Object.keys(preferencesState),
      ...Object.keys(originalPreferencesState),
    ]);

    for (const key of allPrefKeys) {
      const current = preferencesState[key];
      const original = originalPreferencesState[key];
      if (JSON.stringify(current || {}) !== JSON.stringify(original || {})) {
        // Only count if not already counted from hours
        if (
          !allKeys.has(key) ||
          hoursState[key]?.toString() === originalHoursState[key]?.toString()
        ) {
          count++;
        }
      }
    }

    return count;
  };

  // Validate hours value
  const isValidHours = (value: string | number | undefined): boolean => {
    if (value === undefined || value === "") return true;
    const num = parseFloat(value.toString());
    if (isNaN(num)) return false;
    return num >= 0 && num <= 24;
  };

  // Get validation error message for a cell
  const getHoursError = (value: string | number | undefined): string => {
    if (value === undefined || value === "") return "";
    const num = parseFloat(value.toString());
    if (isNaN(num)) return "Invalid number";
    if (num < 0) return "Cannot be negative";
    if (num > 24) return "Cannot exceed 24";
    return "";
  };

  // Check if there are any validation errors
  const hasValidationErrors = (): boolean => {
    for (const key of Object.keys(hoursState)) {
      if (!isValidHours(hoursState[key])) return true;
    }
    return false;
  };

  // Get count of validation errors
  const getValidationErrorsCount = (): number => {
    let count = 0;
    for (const key of Object.keys(hoursState)) {
      if (!isValidHours(hoursState[key])) count++;
    }
    return count;
  };

  // Revert all changes
  const handleRevertChanges = () => {
    setHoursState({ ...originalHoursState });
    setPreferencesState({ ...originalPreferencesState });
    enqueueSnackbar("Changes reverted", { variant: "info" });
  };

  // Check if a specific cell has changes
  const cellHasChanges = (employeeId: string, date: Date): boolean => {
    const key = getStateKey(employeeId, date);
    const currentVal = hoursState[key]?.toString() || "";
    const originalVal = originalHoursState[key]?.toString() || "";
    if (currentVal !== originalVal) return true;

    // Check preferences changes
    const currentPrefs = preferencesState[key];
    const originalPrefs = originalPreferencesState[key];
    if (
      JSON.stringify(currentPrefs || {}) !== JSON.stringify(originalPrefs || {})
    ) {
      return true;
    }

    return false;
  };

  // Revert a specific cell
  const handleRevertCell = (employeeId: string, date: Date) => {
    const key = getStateKey(employeeId, date);
    const originalVal = originalHoursState[key];
    setHoursState((prev) => {
      const newState = { ...prev };
      if (originalVal !== undefined) {
        newState[key] = originalVal;
      } else {
        delete newState[key];
      }
      return newState;
    });

    // Also revert preferences
    const originalPrefs = originalPreferencesState[key];
    setPreferencesState((prev) => {
      const newState = { ...prev };
      if (originalPrefs !== undefined) {
        newState[key] = { ...originalPrefs };
      } else {
        delete newState[key];
      }
      return newState;
    });
  };

  // Check if an employee row has changes
  const employeeHasChanges = (employeeId: string): boolean => {
    for (const date of weekDates) {
      if (cellHasChanges(employeeId, date)) return true;
    }
    return false;
  };

  // Get count of changes for an employee
  const getEmployeeChangesCount = (employeeId: string): number => {
    let count = 0;
    for (const date of weekDates) {
      if (cellHasChanges(employeeId, date)) count++;
    }
    return count;
  };

  // Calculate total hours for an employee for the current week
  const getEmployeeTotalHours = (employeeId: string): number => {
    let total = 0;
    for (const date of weekDates) {
      const key = getStateKey(employeeId, date);
      const hoursValue = hoursState[key];
      if (hoursValue !== undefined && hoursValue !== "") {
        const hours = parseFloat(hoursValue.toString());
        if (!isNaN(hours) && hours >= 0) {
          total += hours;
        }
      }
    }
    return total;
  };

  // Revert all changes for an employee
  const handleRevertEmployee = (employeeId: string) => {
    setHoursState((prev) => {
      const newState = { ...prev };
      for (const date of weekDates) {
        const key = getStateKey(employeeId, date);
        const originalVal = originalHoursState[key];
        if (originalVal !== undefined) {
          newState[key] = originalVal;
        } else {
          delete newState[key];
        }
      }
      return newState;
    });

    // Also revert preferences for all days
    setPreferencesState((prev) => {
      const newState = { ...prev };
      for (const date of weekDates) {
        const key = getStateKey(employeeId, date);
        const originalPrefs = originalPreferencesState[key];
        if (originalPrefs !== undefined) {
          newState[key] = { ...originalPrefs };
        } else {
          delete newState[key];
        }
      }
      return newState;
    });
  };

  // Save all changes using bulk updates
  const handleSaveAllChanges = async () => {
    setIsSaving(true);
    try {
      // Collect all keys that have changes (hours or preferences)
      const allKeys = new Set([
        ...Object.keys(hoursState),
        ...Object.keys(originalHoursState),
        ...Object.keys(preferencesState),
        ...Object.keys(originalPreferencesState),
      ]);

      // Group changes by employee for bulk updates
      const changesByEmployee: Record<
        string,
        Array<{
          date: string;
          availability: {
            date: string;
            hoursAvailable: number;
            shopId: string;
            timeRestrictions?: TimeRestrictions;
            notes?: string;
          } | null;
        }>
      > = {};

      for (const key of allKeys) {
        const currentHours = hoursState[key]?.toString() || "";
        const originalHours = originalHoursState[key]?.toString() || "";
        const currentPrefs = preferencesState[key];
        const originalPrefs = originalPreferencesState[key];

        const hoursChanged = currentHours !== originalHours;
        const prefsChanged =
          JSON.stringify(currentPrefs || {}) !==
          JSON.stringify(originalPrefs || {});

        if (hoursChanged || prefsChanged) {
          // Parse key to get employeeId and date
          const [employeeId, dateKey] = key.split("_");
          const employee = employees.find((e) => e.id === employeeId);
          if (!employee) continue;

          if (!changesByEmployee[employeeId]) {
            changesByEmployee[employeeId] = [];
          }

          const hours = parseFloat(currentHours || "0");

          if (isNaN(hours) || hours < 0 || currentHours === "") {
            // Remove availability if no hours and no preferences
            if (!currentPrefs?.timeRestrictions && !currentPrefs?.notes) {
              const existingAvailability = employeesService.getDateAvailability(
                employee,
                dateKey
              );
              if (existingAvailability) {
                changesByEmployee[employeeId].push({
                  date: dateKey,
                  availability: null,
                });
              }
            }
          } else {
            // Save or update availability with local preferences
            const shopId =
              employee.assignedShopIds?.[0] || shops[0]?.id || "default";

            changesByEmployee[employeeId].push({
              date: dateKey,
              availability: {
                date: dateKey,
                hoursAvailable: hours,
                shopId,
                timeRestrictions: currentPrefs?.timeRestrictions,
                notes: currentPrefs?.notes,
              },
            });
          }
        }
      }

      // Perform bulk updates for each employee
      for (const [employeeId, updates] of Object.entries(changesByEmployee)) {
        if (updates.length > 0) {
          await updateMultipleDateAvailability(employeeId, updates);
        }
      }

      // Update original states to match current
      setOriginalHoursState({ ...hoursState });
      setOriginalPreferencesState({ ...preferencesState });
      enqueueSnackbar("All changes saved successfully!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to save some changes", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if a date is in the past (before today)
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Check if editing is allowed for a specific date
  const canEditDate = (date: Date): boolean => {
    if (!canWrite) return false;
    if (allowEditHistory) return true;
    return !isPastDate(date);
  };

  // Navigation handler for DateNavigator
  const handleDateChange = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    setWeekStart(date);
  };

  // Get employee shop names
  const getEmployeeShops = (employee: Employee) => {
    if (!employee.assignedShopIds || employee.assignedShopIds.length === 0) {
      return "No shops";
    }
    if (employee.assignedShopIds.length === shops.length) {
      return "All Shops";
    }
    return employee.assignedShopIds
      .map((id) => shops.find((s) => s.id === id)?.name)
      .filter(Boolean)
      .join(", ");
  };

  // Handle hours change
  const handleHoursChange = (employee: Employee, date: Date, value: string) => {
    const key = getStateKey(employee.id, date);
    setHoursState((prev) => ({ ...prev, [key]: value }));
  };

  // Open preferences dialog
  const handleOpenPreferences = (employee: Employee, date: Date) => {
    if (!canEditDate(date) || employee.status !== "active") return;

    setSelectedEmployee(employee);
    setSelectedDate(date);

    const key = getStateKey(employee.id, date);
    const localPrefs = preferencesState[key];

    // Use local state first, fall back to saved data
    const dateKey = formatDate(date);
    const existingAvailability = employeesService.getDateAvailability(
      employee,
      dateKey
    );

    setPreferencesForm({
      timeRestrictions: {
        morning:
          localPrefs?.timeRestrictions?.morning ??
          existingAvailability?.timeRestrictions?.morning ??
          false,
        afternoon:
          localPrefs?.timeRestrictions?.afternoon ??
          existingAvailability?.timeRestrictions?.afternoon ??
          false,
        evening:
          localPrefs?.timeRestrictions?.evening ??
          existingAvailability?.timeRestrictions?.evening ??
          false,
        night:
          localPrefs?.timeRestrictions?.night ??
          existingAvailability?.timeRestrictions?.night ??
          false,
      },
      notes: localPrefs?.notes ?? existingAvailability?.notes ?? "",
    });

    setPreferencesOpen(true);
  };

  // Save preferences to local state only
  const handleSavePreferences = () => {
    if (!selectedEmployee || !selectedDate) return;

    const key = getStateKey(selectedEmployee.id, selectedDate);

    // Build time restrictions object
    const hasRestrictions = Object.values(
      preferencesForm.timeRestrictions
    ).some((v) => v);

    const newPrefs: { timeRestrictions?: TimeRestrictions; notes?: string } =
      {};

    if (hasRestrictions) {
      newPrefs.timeRestrictions = {};
      if (preferencesForm.timeRestrictions.morning) {
        newPrefs.timeRestrictions.morning = true;
      }
      if (preferencesForm.timeRestrictions.afternoon) {
        newPrefs.timeRestrictions.afternoon = true;
      }
      if (preferencesForm.timeRestrictions.evening) {
        newPrefs.timeRestrictions.evening = true;
      }
      if (preferencesForm.timeRestrictions.night) {
        newPrefs.timeRestrictions.night = true;
      }
    }

    if (preferencesForm.notes && preferencesForm.notes.trim()) {
      newPrefs.notes = preferencesForm.notes.trim();
    }

    // Update local preferences state
    setPreferencesState((prev) => {
      const newState = { ...prev };
      if (Object.keys(newPrefs).length > 0) {
        newState[key] = newPrefs;
      } else {
        delete newState[key];
      }
      return newState;
    });

    enqueueSnackbar("Preferences updated (unsaved)", { variant: "info" });
    setPreferencesOpen(false);
  };

  // Copy employee's week availability (including local unsaved changes)
  const handleCopyEmployee = (employee: Employee) => {
    clearAllClipboard();

    const weekStartDate = formatDate(weekDates[0]);
    const weekEndDate = formatDate(weekDates[6]);
    const weekNum = getWeekNumber(weekDates[0]);

    const availability: Record<string, DateAvailability> = {};

    weekDates.forEach((date) => {
      const key = getStateKey(employee.id, date);
      const dateKey = formatDate(date);
      const hoursValue = hoursState[key];
      const localPrefs = preferencesState[key];
      const savedAvailability = employeesService.getDateAvailability(
        employee,
        dateKey
      );

      // Build availability from local state if exists, otherwise use saved
      const hours =
        hoursValue !== undefined
          ? parseFloat(hoursValue.toString() || "0")
          : savedAvailability?.hoursAvailable;

      if (hours !== undefined && hours > 0) {
        availability[dateKey] = {
          date: dateKey,
          shopId:
            savedAvailability?.shopId || employee.assignedShopIds?.[0] || "",
          hoursAvailable: hours,
          timeRestrictions:
            localPrefs?.timeRestrictions ?? savedAvailability?.timeRestrictions,
          notes: localPrefs?.notes ?? savedAvailability?.notes,
        };
      }
    });

    setCopiedEmployeeData({
      employeeId: employee.id,
      employeeName: employee.name,
      weekStart: new Date(weekStart),
      weekStartDate,
      weekEndDate,
      weekNumber: weekNum,
      availability,
    });

    enqueueSnackbar(
      `Copied ${employee.name}'s availability (Week ${weekNum}: ${formatDateRange(weekStartDate, weekEndDate)})`,
      { variant: "info" }
    );
  };

  // Copy entire week's availability for all employees (including local unsaved changes)
  const handleCopyWeek = () => {
    clearAllClipboard();

    const weekStartDate = formatDate(weekDates[0]);
    const weekEndDate = formatDate(weekDates[6]);
    const weekNum = getWeekNumber(weekDates[0]);

    const employeesAvailability: Record<
      string,
      Record<string, DateAvailability>
    > = {};

    employees.forEach((employee) => {
      const availability: Record<string, DateAvailability> = {};
      weekDates.forEach((date) => {
        const key = getStateKey(employee.id, date);
        const dateKey = formatDate(date);
        const hoursValue = hoursState[key];
        const localPrefs = preferencesState[key];
        const savedAvailability = employeesService.getDateAvailability(
          employee,
          dateKey
        );

        // Build availability from local state if exists, otherwise use saved
        const hours =
          hoursValue !== undefined
            ? parseFloat(hoursValue.toString() || "0")
            : savedAvailability?.hoursAvailable;

        if (hours !== undefined && hours > 0) {
          availability[dateKey] = {
            date: dateKey,
            shopId:
              savedAvailability?.shopId || employee.assignedShopIds?.[0] || "",
            hoursAvailable: hours,
            timeRestrictions:
              localPrefs?.timeRestrictions ??
              savedAvailability?.timeRestrictions,
            notes: localPrefs?.notes ?? savedAvailability?.notes,
          };
        }
      });
      if (Object.keys(availability).length > 0) {
        employeesAvailability[employee.id] = availability;
      }
    });

    setCopiedWeekData({
      weekStart: new Date(weekStart),
      weekStartDate,
      weekEndDate,
      weekNumber: weekNum,
      employeesAvailability,
    });

    enqueueSnackbar(
      `Copied entire week's availability (Week ${weekNum}: ${formatDateRange(weekStartDate, weekEndDate)})`,
      { variant: "info" }
    );
  };

  // Paste employee's week availability (local state only)
  const handlePasteEmployee = (targetEmployee: Employee) => {
    if (!copiedEmployeeData) return;

    const sourceWeekStart = copiedEmployeeData.weekStart;
    const targetWeekStart = weekStart;

    // Calculate day offset between source and target weeks
    const dayOffset = Math.floor(
      (targetWeekStart.getTime() - sourceWeekStart.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    const newHoursState = { ...hoursState };
    const newPrefsState = { ...preferencesState };

    // Paste availability for each day to local state
    for (const [sourceDateKey, availability] of Object.entries(
      copiedEmployeeData.availability
    )) {
      const sourceDate = new Date(sourceDateKey);
      const targetDate = new Date(sourceDate);
      targetDate.setDate(sourceDate.getDate() + dayOffset);
      const key = getStateKey(targetEmployee.id, targetDate);

      // Update hours
      newHoursState[key] = availability.hoursAvailable;

      // Update preferences if any
      if (availability.timeRestrictions || availability.notes) {
        newPrefsState[key] = {
          timeRestrictions: availability.timeRestrictions,
          notes: availability.notes,
        };
      } else {
        delete newPrefsState[key];
      }
    }

    setHoursState(newHoursState);
    setPreferencesState(newPrefsState);

    enqueueSnackbar(
      `Pasted ${copiedEmployeeData.employeeName}'s availability to ${targetEmployee.name} (unsaved)`,
      { variant: "info" }
    );
  };

  // Paste entire week's availability (local state only)
  const handlePasteWeek = () => {
    if (!copiedWeekData) return;

    const sourceWeekStart = copiedWeekData.weekStart;
    const targetWeekStart = weekStart;

    // Calculate day offset
    const dayOffset = Math.floor(
      (targetWeekStart.getTime() - sourceWeekStart.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    const newHoursState = { ...hoursState };
    const newPrefsState = { ...preferencesState };
    let pastedCount = 0;

    // Paste availability for each employee to local state
    for (const [employeeId, availability] of Object.entries(
      copiedWeekData.employeesAvailability
    )) {
      const employee = employees.find((emp) => emp.id === employeeId);
      if (!employee) continue;

      for (const [sourceDateKey, dateAvailability] of Object.entries(
        availability
      )) {
        const sourceDate = new Date(sourceDateKey);
        const targetDate = new Date(sourceDate);
        targetDate.setDate(sourceDate.getDate() + dayOffset);
        const key = getStateKey(employeeId, targetDate);

        // Update hours
        newHoursState[key] = dateAvailability.hoursAvailable;

        // Update preferences if any
        if (dateAvailability.timeRestrictions || dateAvailability.notes) {
          newPrefsState[key] = {
            timeRestrictions: dateAvailability.timeRestrictions,
            notes: dateAvailability.notes,
          };
        } else {
          delete newPrefsState[key];
        }

        pastedCount++;
      }
    }

    setHoursState(newHoursState);
    setPreferencesState(newPrefsState);

    enqueueSnackbar(
      `Pasted availability for ${pastedCount} entries (unsaved)`,
      {
        variant: "info",
      }
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (
    e: React.KeyboardEvent,
    employeeIndex: number,
    dateIndex: number
  ) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const activeEmployees = employees.filter(
        (emp) => emp.status === "active"
      );

      // Find next/previous editable cell (skipping disabled ones)
      const findNextEditableCell = (
        startEmpIndex: number,
        startDateIndex: number,
        forward: boolean
      ): { empIndex: number; dateIdx: number } | null => {
        let empIdx = startEmpIndex;
        let dateIdx = startDateIndex;

        while (true) {
          if (forward) {
            dateIdx++;
            if (dateIdx >= 7) {
              dateIdx = 0;
              empIdx++;
            }
            if (empIdx >= activeEmployees.length) {
              return null; // No more cells
            }
          } else {
            dateIdx--;
            if (dateIdx < 0) {
              dateIdx = 6;
              empIdx--;
            }
            if (empIdx < 0) {
              return null; // No more cells
            }
          }

          // Check if this cell is editable
          const date = weekDates[dateIdx];
          if (canEditDate(date)) {
            return { empIndex: empIdx, dateIdx };
          }
        }
      };

      if (e.shiftKey) {
        // Shift+Tab: Move backward to previous editable cell
        const prev = findNextEditableCell(employeeIndex, dateIndex, false);
        if (prev) {
          const prevInput = document.getElementById(
            `hours-${prev.empIndex}-${prev.dateIdx}`
          );
          prevInput?.focus();
        }
      } else {
        // Tab: Move forward to next editable cell
        const next = findNextEditableCell(employeeIndex, dateIndex, true);
        if (next) {
          const nextInput = document.getElementById(
            `hours-${next.empIndex}-${next.dateIdx}`
          );
          nextInput?.focus();
        }
      }
    }
  };

  // Get restriction labels
  const getRestrictionLabel = (restrictions?: TimeRestrictions): string => {
    if (!restrictions) return "";
    const periods: string[] = [];
    if (restrictions.morning) periods.push("M");
    if (restrictions.afternoon) periods.push("A");
    if (restrictions.evening) periods.push("E");
    if (restrictions.night) periods.push("N");
    return periods.join("+");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (employees.length === 0) {
    return (
      <Paper sx={{ p: 8, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No employees found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add employees to view their availability
        </Typography>
      </Paper>
    );
  }

  // Filter active employees
  const activeEmployees = employees.filter((emp) => emp.status === "active");

  const changesCount = getChangesCount();

  // Render minimized sidebar
  const renderMinimizedSidebar = () => (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 64,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
        zIndex: 10,
        borderRadius: 0,
      }}
    >
      <Tooltip title="Expand Panel" placement="right">
        <IconButton onClick={() => setSidebarOpen(true)} sx={{ mb: 2 }}>
          <MenuIcon />
        </IconButton>
      </Tooltip>

      <Divider sx={{ width: "100%", my: 1 }} />

      {canWrite && (
        <>
          <Tooltip
            title={hasValidationErrors() ? "Fix errors first" : "Save Changes"}
            placement="right"
          >
            <span>
              <IconButton
                onClick={handleSaveAllChanges}
                disabled={!hasChanges() || isSaving || hasValidationErrors()}
                color={
                  hasValidationErrors()
                    ? "error"
                    : hasChanges()
                    ? "primary"
                    : "default"
                }
                sx={{ mb: 1 }}
              >
                <Save />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Revert Changes" placement="right">
            <span>
              <IconButton
                onClick={handleRevertChanges}
                disabled={!hasChanges()}
                color={hasChanges() ? "warning" : "default"}
              >
                <Undo />
              </IconButton>
            </span>
          </Tooltip>

          {hasValidationErrors() ? (
            <Chip
              label={getValidationErrorsCount()}
              size="small"
              color="error"
              sx={{ mt: 1, minWidth: 24, height: 24 }}
            />
          ) : hasChanges() ? (
            <Chip
              label={changesCount}
              size="small"
              color="primary"
              sx={{ mt: 1, minWidth: 24, height: 24 }}
            />
          ) : null}
        </>
      )}
    </Paper>
  );

  // Render expanded sidebar
  const renderExpandedSidebar = () => (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 280,
        p: 2,
        overflow: "auto",
        zIndex: 10,
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Actions
        </Typography>
        <IconButton size="small" onClick={() => setSidebarOpen(false)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {canWrite && (
        <>
          {/* Save/Revert Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Changes
            </Typography>
            {hasValidationErrors() ? (
              <Chip
                label={`${getValidationErrorsCount()} error${
                  getValidationErrorsCount() !== 1 ? "s" : ""
                }`}
                size="small"
                color="error"
              />
            ) : hasChanges() ? (
              <Chip
                label={`${changesCount} unsaved`}
                size="small"
                color="warning"
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No changes
              </Typography>
            )}
          </Box>

          <Tooltip
            title={
              hasValidationErrors() ? "Fix validation errors before saving" : ""
            }
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSaveAllChanges}
                disabled={!hasChanges() || isSaving || hasValidationErrors()}
                fullWidth
                sx={{ mb: 1 }}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<Undo />}
            onClick={handleRevertChanges}
            disabled={!hasChanges()}
            fullWidth
            color="warning"
            sx={{ mb: 3 }}
          >
            Revert Changes
          </Button>

          <Divider sx={{ mb: 2 }} />

          {/* Copy/Paste Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Copy / Paste
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopy />}
              onClick={handleCopyWeek}
              fullWidth
            >
              Copy Week
            </Button>
            <Tooltip
              title={copiedWeekData ? "" : "Nothing copied"}
              placement="top"
            >
              <span style={{ width: "100%" }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ContentPaste />}
                  onClick={handlePasteWeek}
                  fullWidth
                  color={copiedWeekData ? "info" : "inherit"}
                  disabled={!copiedWeekData}
                >
                  Paste Week
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Clipboard Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Clipboard
            </Typography>
            {(copiedEmployeeData || copiedWeekData) && (
              <Tooltip title="Clear clipboard">
                <IconButton size="small" onClick={clearAllClipboard}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {copiedWeekData ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "info.50",
                border: "1px solid",
                borderColor: "info.main",
                mb: 2,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <ContentCopy fontSize="small" color="info" />
                <Typography variant="body2" fontWeight={500}>
                  Entire Week Copied
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatDateRange(
                  copiedWeekData.weekStartDate,
                  copiedWeekData.weekEndDate
                )}{" "}
                (Week {copiedWeekData.weekNumber})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Object.keys(copiedWeekData.employeesAvailability).length}{" "}
                employee(s)
              </Typography>
            </Box>
          ) : copiedEmployeeData ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "info.50",
                border: "1px solid",
                borderColor: "info.main",
                mb: 2,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <ContentCopy fontSize="small" color="info" />
                <Typography variant="body2" fontWeight={500}>
                  Employee Week Copied
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatDateRange(
                  copiedEmployeeData.weekStartDate,
                  copiedEmployeeData.weekEndDate
                )}{" "}
                (Week {copiedEmployeeData.weekNumber})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {copiedEmployeeData.employeeName} •{" "}
                {Object.keys(copiedEmployeeData.availability).length} day(s)
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              fontStyle="italic"
              sx={{ mb: 2 }}
            >
              Use copy buttons on rows to copy availability
            </Typography>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Modified Cells List */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Modified Cells
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {!hasChanges() ? (
              <Typography
                variant="body2"
                color="text.disabled"
                fontStyle="italic"
              >
                No pending changes
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {activeEmployees
                  .filter((emp) => employeeHasChanges(emp.id))
                  .map((emp) => (
                    <Box
                      key={emp.id}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "warning.50",
                        border: "1px solid",
                        borderColor: "warning.light",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" fontWeight={500}>
                          {emp.name}
                        </Typography>
                        <Chip
                          label={`${getEmployeeChangesCount(emp.id)} change${
                            getEmployeeChangesCount(emp.id) !== 1 ? "s" : ""
                          }`}
                          size="small"
                          color="warning"
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Help Text */}
      <Box sx={{ mt: "auto", pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          {canWrite
            ? "Enter hours available per day. Use the settings icon for time restrictions."
            : "View-only mode. Contact an admin to edit availability."}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      {/* Sidebar */}
      {sidebarOpen ? renderExpandedSidebar() : renderMinimizedSidebar()}

      {/* Main content with margin for fixed sidebar */}
      <Box sx={{ ml: sidebarOpen ? "280px" : "64px" }}>
        {/* Week Navigator */}
        <DateNavigator
          mode="weekly"
          selectedDate={formatDate(weekStart)}
          onDateChange={handleDateChange}
        />

        {/* Calendar Table */}
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: 200,
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 3,
                  }}
                >
                  Employee
                </TableCell>
                {weekDates.map((date, index) => {
                  const day = dayOfWeekOrder[index];
                  const isTodayCell = isToday(date);
                  return (
                    <TableCell
                      key={day}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={isTodayCell ? "primary.main" : "text.primary"}
                      >
                        {dayLabels[day]}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={isTodayCell ? "primary.main" : "text.primary"}
                      >
                        {date.getDate()}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeEmployees.map((employee, employeeIndex) => (
                <TableRow key={employee.id} hover>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Person
                          fontSize="small"
                          sx={{ color: "primary.main" }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {employee.name}
                        </Typography>
                        {canWrite && (
                          <>
                            <Tooltip title="Copy this employee's week">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyEmployee(employee)}
                                sx={{ p: 0.25 }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {copiedEmployeeData && (
                              <Tooltip
                                title={`Paste ${copiedEmployeeData.employeeName}'s availability here`}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handlePasteEmployee(employee)}
                                  sx={{ p: 0.25 }}
                                  color="primary"
                                >
                                  <ContentPaste fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {employeeHasChanges(employee.id) && (
                              <Tooltip
                                title={`Revert ${getEmployeeChangesCount(
                                  employee.id
                                )} change(s) for this employee`}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleRevertEmployee(employee.id)
                                  }
                                  sx={{ p: 0.25 }}
                                  color="warning"
                                >
                                  <Undo fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {getEmployeeShops(employee)}
                        </Typography>
                        {employeeHasChanges(employee.id) && (
                          <Chip
                            label={`${getEmployeeChangesCount(
                              employee.id
                            )} changed`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </Box>
                      {/* Total Hours Display */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Schedule fontSize="small" sx={{ color: "info.main", fontSize: 14 }} />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="info.main"
                        >
                          Total: {getEmployeeTotalHours(employee.id)}h
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {weekDates.map((date, dateIndex) => {
                    const key = getStateKey(employee.id, date);
                    const hoursValue = hoursState[key] ?? "";
                    const localPrefs = preferencesState[key];
                    const dateKey = formatDate(date);
                    const availability = employeesService.getDateAvailability(
                      employee,
                      dateKey
                    );

                    // Use local preferences state first, fall back to saved data
                    const restrictionLabel = getRestrictionLabel(
                      localPrefs?.timeRestrictions ??
                        availability?.timeRestrictions
                    );

                    const hours = parseFloat(hoursValue?.toString() || "0");
                    const isOff = hours === 0 && hoursValue !== "";
                    const hasCellChanges = cellHasChanges(employee.id, date);
                    const hasLocalData = hoursValue !== "" || localPrefs;
                    const hoursError = getHoursError(hoursValue);
                    const hasError = hoursError !== "";

                    return (
                      <TableCell
                        key={dateIndex}
                        sx={{
                          minWidth: 80,
                          bgcolor: hasError
                            ? "error.lighter"
                            : isOff
                            ? "grey.300"
                            : hasLocalData || availability
                            ? "success.lighter"
                            : "grey.50",
                          p: 1,
                          border: hasError ? 2 : hasCellChanges ? 2 : 0,
                          borderColor: hasError
                            ? "error.main"
                            : hasCellChanges
                            ? "warning.main"
                            : "transparent",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            alignItems: "center",
                          }}
                        >
                          {/* Hours Input */}
                          <TextField
                            id={`hours-${employeeIndex}-${dateIndex}`}
                            type="number"
                            value={hoursValue}
                            onChange={(e) =>
                              handleHoursChange(employee, date, e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleKeyDown(e, employeeIndex, dateIndex)
                            }
                            disabled={!canEditDate(date)}
                            size="small"
                            placeholder="Hours"
                            error={hasError}
                            helperText={hoursError}
                            inputProps={{
                              min: 0,
                              max: 24,
                              step: 0.5,
                              style: { textAlign: "center" },
                              tabIndex: canEditDate(date) ? 0 : -1,
                            }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />

                          {/* OFF/Holiday indicator */}
                          {isOff && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                OFF/Holiday
                              </Typography>
                              {canEditDate(date) && hasCellChanges && (
                                <Tooltip title="Revert this cell">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRevertCell(employee.id, date)
                                    }
                                    color="warning"
                                    sx={{ p: 0.25 }}
                                  >
                                    <Undo fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}

                          {/* Preferences Info & Button */}
                          {!isOff && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                width: "100%",
                              }}
                            >
                              {restrictionLabel && (
                                <Chip
                                  label={restrictionLabel}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ fontSize: "0.6rem", height: 18 }}
                                />
                              )}
                              {canEditDate(date) && (
                                <>
                                  <Tooltip title="Set time restrictions">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleOpenPreferences(employee, date)
                                      }
                                      sx={{ p: 0.25 }}
                                    >
                                      <Settings fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {hasCellChanges && (
                                    <Tooltip title="Revert this cell">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleRevertCell(employee.id, date)
                                        }
                                        color="warning"
                                        sx={{ p: 0.25 }}
                                      >
                                        <Undo fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {employees.filter((emp) => emp.status === "inactive").length >
                0 && (
                <>
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        bgcolor: "grey.100",
                        py: 1,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        Inactive Employees
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {employees
                    .filter((emp) => emp.status === "inactive")
                    .map((employee) => (
                      <TableRow key={employee.id} sx={{ opacity: 0.6 }}>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: "background.paper",
                            zIndex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <PersonOff
                                fontSize="small"
                                sx={{ color: "text.disabled" }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.secondary"
                              >
                                {employee.name}
                              </Typography>
                              <Chip
                                label="Inactive"
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {getEmployeeShops(employee)}
                            </Typography>
                          </Box>
                        </TableCell>
                        {weekDates.map((_, index) => (
                          <TableCell
                            key={index}
                            sx={{
                              minWidth: 140,
                              bgcolor: "grey.200",
                            }}
                          >
                            <Box sx={{ textAlign: "center", py: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                Unavailable
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Preferences Dialog */}
      <Dialog
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Schedule color="primary" />
            <Typography variant="h5" fontWeight={600}>
              Time Restrictions
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Set availability for <strong>{selectedEmployee?.name}</strong> on{" "}
            <strong>
              {selectedDate?.toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </strong>
          </Typography>
          {/* Close button */}
          <IconButton
            onClick={() => setPreferencesOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {/* Quick Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              QUICK ACTIONS:
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setPreferencesForm({
                  ...preferencesForm,
                  timeRestrictions: {
                    morning: true,
                    afternoon: true,
                    evening: true,
                    night: true,
                  },
                })
              }
              startIcon={<CheckCircle fontSize="small" />}
              sx={{ minWidth: "auto" }}
            >
              Select All
            </Button>
            <Divider orientation="vertical" flexItem />
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setPreferencesForm({
                  ...preferencesForm,
                  timeRestrictions: {
                    morning: false,
                    afternoon: false,
                    evening: false,
                    night: false,
                  },
                })
              }
              startIcon={<RadioButtonUnchecked fontSize="small" />}
              sx={{ minWidth: "auto" }}
            >
              Clear All
            </Button>
          </Box>

          {/* Time Periods */}
          <Stack spacing={1.5}>
            {/* Morning - Soft Sky Blue #4C7EDB */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                transition: "all 0.2s",
                bgcolor: preferencesForm.timeRestrictions.morning
                  ? "background.paper"
                  : "grey.50",
                borderLeft: preferencesForm.timeRestrictions.morning ? 4 : 0,
                borderLeftColor: "#4C7EDB",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.morning
                      ? "rgba(76, 126, 219, 0.1)"
                      : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  <LightMode
                    sx={{
                      color: preferencesForm.timeRestrictions.morning
                        ? "#4C7EDB"
                        : "text.disabled",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: preferencesForm.timeRestrictions.morning
                        ? "#4C7EDB"
                        : "text.secondary",
                    }}
                  >
                    Morning
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    6:00 AM - 12:00 PM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.morning}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: {
                        ...preferencesForm.timeRestrictions,
                        morning: e.target.checked,
                      },
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#4C7EDB",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#4C7EDB",
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Afternoon - Warm Amber #D9A441 */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                transition: "all 0.2s",
                bgcolor: preferencesForm.timeRestrictions.afternoon
                  ? "background.paper"
                  : "grey.50",
                borderLeft: preferencesForm.timeRestrictions.afternoon ? 4 : 0,
                borderLeftColor: "#D9A441",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.afternoon
                      ? "rgba(217, 164, 65, 0.1)"
                      : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  <WbTwilight
                    sx={{
                      color: preferencesForm.timeRestrictions.afternoon
                        ? "#D9A441"
                        : "text.disabled",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: preferencesForm.timeRestrictions.afternoon
                        ? "#D9A441"
                        : "text.secondary",
                    }}
                  >
                    Afternoon
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    12:00 PM - 6:00 PM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.afternoon}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: {
                        ...preferencesForm.timeRestrictions,
                        afternoon: e.target.checked,
                      },
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#D9A441",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#D9A441",
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Evening - Muted Violet #7B61B3 */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                transition: "all 0.2s",
                bgcolor: preferencesForm.timeRestrictions.evening
                  ? "background.paper"
                  : "grey.50",
                borderLeft: preferencesForm.timeRestrictions.evening ? 4 : 0,
                borderLeftColor: "#7B61B3",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.evening
                      ? "rgba(123, 97, 179, 0.1)"
                      : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  <Nightlight
                    sx={{
                      color: preferencesForm.timeRestrictions.evening
                        ? "#7B61B3"
                        : "text.disabled",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: preferencesForm.timeRestrictions.evening
                        ? "#7B61B3"
                        : "text.secondary",
                    }}
                  >
                    Evening
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    6:00 PM - 12:00 AM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.evening}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: {
                        ...preferencesForm.timeRestrictions,
                        evening: e.target.checked,
                      },
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#7B61B3",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#7B61B3",
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Night - Deep Slate Blue #2F3A56 */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                transition: "all 0.2s",
                bgcolor: preferencesForm.timeRestrictions.night
                  ? "background.paper"
                  : "grey.50",
                borderLeft: preferencesForm.timeRestrictions.night ? 4 : 0,
                borderLeftColor: "#2F3A56",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.night
                      ? "rgba(47, 58, 86, 0.1)"
                      : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  <DarkMode
                    sx={{
                      color: preferencesForm.timeRestrictions.night
                        ? "#2F3A56"
                        : "text.disabled",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: preferencesForm.timeRestrictions.night
                        ? "#2F3A56"
                        : "text.secondary",
                    }}
                  >
                    Night
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    12:00 AM - 6:00 AM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.night}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: {
                        ...preferencesForm.timeRestrictions,
                        night: e.target.checked,
                      },
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#2F3A56",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2F3A56",
                    },
                  }}
                />
              </Box>
            </Paper>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, mb: 2.5 }}
          >
            Leave all unchecked if available any time. Select multiple for
            custom combinations.
          </Typography>

          {/* Notes */}
          <TextField
            label="Notes (Optional)"
            multiline
            rows={3}
            value={preferencesForm.notes}
            onChange={(e) =>
              setPreferencesForm({
                ...preferencesForm,
                notes: e.target.value,
              })
            }
            fullWidth
            placeholder="Any additional restrictions or notes..."
          />
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPreferencesOpen(false)} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleSavePreferences}
            variant="contained"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Set
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
